import React, { useState, useEffect } from 'react';
import { useAsset } from '../../context/AssetContext';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../ui/dropdown-menu';
import { Skeleton } from '../ui/skeleton';
import { Checkbox } from '../ui/checkbox';
import { ScrollArea } from '../ui/scroll-area';
import { toast } from 'sonner';
import { Plus, Search, MoreHorizontal, Pencil, Trash2, Users, Loader2 } from 'lucide-react';

const ProjectList = () => {
  const { 
    projects, employees, isReady,
    fetchProjects, fetchEmployees, createProject, updateProject, deleteProject,
    allocateEmployeesToProject
  } = useAsset();

  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ page: 1, page_size: 50, total: 0, total_pages: 0 });
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');

  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [allocateDialogOpen, setAllocateDialogOpen] = useState(false);

  const [selectedProject, setSelectedProject] = useState(null);
  const [formLoading, setFormLoading] = useState(false);

  const [formData, setFormData] = useState({
    project_id: '',
    project_name: '',
    is_active: true
  });

  const [selectedEmployees, setSelectedEmployees] = useState([]);

  useEffect(() => {
    if (isReady) {
      loadProjects();
      fetchEmployees();
    }
  }, [isReady]);

  useEffect(() => {
    if (!isReady) return;
    const timer = setTimeout(() => {
      setCurrentPage(1);
      loadProjects(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery, isReady]);

  const loadProjects = async (page = currentPage) => {
    setLoading(true);
    const filters = {};
    if (searchQuery) filters.search = searchQuery;
    const result = await fetchProjects(filters, page, 50);
    if (result.pagination) setPagination(result.pagination);
    setLoading(false);
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    loadProjects(newPage);
  };

  const resetForm = () => {
    setFormData({ project_id: '', project_name: '', is_active: true });
  };

  const handleCreate = async () => {
    if (!formData.project_id || !formData.project_name) {
      toast.error('Please fill in all required fields');
      return;
    }

    setFormLoading(true);
    const result = await createProject(formData);
    setFormLoading(false);

    if (result.success) {
      toast.success('Project created successfully');
      setCreateDialogOpen(false);
      resetForm();
      loadProjects();
    } else {
      toast.error(result.error);
    }
  };

  const handleEdit = async () => {
    setFormLoading(true);
    const { project_id, ...updateData } = formData;
    const result = await updateProject(selectedProject.project_id, updateData);
    setFormLoading(false);

    if (result.success) {
      toast.success('Project updated successfully');
      setEditDialogOpen(false);
      resetForm();
      loadProjects();
    } else {
      toast.error(result.error);
    }
  };

  const handleDelete = async () => {
    setFormLoading(true);
    const result = await deleteProject(selectedProject.project_id);
    setFormLoading(false);

    if (result.success) {
      toast.success('Project deleted successfully');
      setDeleteDialogOpen(false);
      loadProjects();
    } else {
      toast.error(result.error);
    }
  };

  const handleAllocateEmployees = async () => {
    setFormLoading(true);
    const result = await allocateEmployeesToProject(selectedProject.project_id, selectedEmployees);
    setFormLoading(false);

    if (result.success) {
      toast.success('Employees allocated successfully');
      setAllocateDialogOpen(false);
      loadProjects();
    } else {
      toast.error(result.error);
    }
  };

  const openEditDialog = (project) => {
    setSelectedProject(project);
    setFormData({ 
      project_id: project.project_id,
      project_name: project.project_name,
      is_active: project.is_active 
    });
    setEditDialogOpen(true);
  };

  const openDeleteDialog = (project) => {
    setSelectedProject(project);
    setDeleteDialogOpen(true);
  };

  const openAllocateDialog = (project) => {
    setSelectedProject(project);
    const currentIds = (project.allocated_employees || []).map(e => e.employee_id);
    setSelectedEmployees(currentIds);
    setAllocateDialogOpen(true);
  };

  const toggleEmployee = (employeeId) => {
    setSelectedEmployees(prev => 
      prev.includes(employeeId) 
        ? prev.filter(id => id !== employeeId)
        : [...prev, employeeId]
    );
  };

  const clearAllEmployees = () => {
    setSelectedEmployees([]);
  };

  const employedEmployees = Array.isArray(employees) ? employees.filter(e => e.employee_status === 'Employed') : [];

  return (
    <div className="space-y-6" data-testid="project-list">
      <div className="flex flex-col sm:flex-row justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Projects</h2>
          <p className="text-muted-foreground">Manage projects and employee allocations</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => setCreateDialogOpen(true)}>
            <Plus className="mr-2 h-4 w-4" /> Add Project
          </Button>
        </div>
      </div>

      {/* Search */}
      <Card>
        <CardContent className="pt-6">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search by Project ID or Name..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>Projects</span>
            <Badge variant="secondary">{pagination?.total || 0} records</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-2">
              {[1, 2, 3, 4, 5].map(i => <Skeleton key={i} className="h-12 w-full" />)}
            </div>
          ) : !Array.isArray(projects) || projects.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              No projects found. Add your first project to get started.
            </div>
          ) : (
            <div className="rounded-md border overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Project ID</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Allocated Employees</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="w-[50px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {projects.map((project) => (
                    <TableRow key={project?.project_id || Math.random()}>
                      <TableCell className="font-mono">{project?.project_id}</TableCell>
                      <TableCell className="font-medium">{project?.project_name}</TableCell>
                      <TableCell>
                        {(project?.allocated_employees || []).length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {project.allocated_employees.slice(0, 2).map(e => (
                              <Badge key={e.employee_id} variant="outline" className="text-xs">
                                {e.employee_name}
                              </Badge>
                            ))}
                            {project.allocated_employees.length > 2 && (
                              <Badge variant="secondary" className="text-xs">
                                +{project.allocated_employees.length - 2} more
                              </Badge>
                            )}
                          </div>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge variant={project?.is_active ? 'default' : 'secondary'}>
                          {project?.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon"><MoreHorizontal className="h-4 w-4" /></Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => openAllocateDialog(project)}>
                              <Users className="mr-2 h-4 w-4" /> Allocate Employees
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => openEditDialog(project)}>
                              <Pencil className="mr-2 h-4 w-4" /> Edit
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => openDeleteDialog(project)} className="text-red-600">
                              <Trash2 className="mr-2 h-4 w-4" /> Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          {!loading && Array.isArray(projects) && projects.length > 0 && pagination?.total_pages > 1 && (
            <div className="flex items-center justify-between mt-4 pt-4 border-t">
              <p className="text-sm text-muted-foreground">Page {pagination.page} of {pagination.total_pages}</p>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => handlePageChange(pagination.page - 1)} disabled={pagination.page <= 1}>Previous</Button>
                <Button variant="outline" size="sm" onClick={() => handlePageChange(pagination.page + 1)} disabled={pagination.page >= pagination.total_pages}>Next</Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={createDialogOpen || editDialogOpen} onOpenChange={(open) => {
        if (!open) { setCreateDialogOpen(false); setEditDialogOpen(false); resetForm(); }
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editDialogOpen ? 'Edit Project' : 'Add New Project'}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <Label>Project ID *</Label>
              <Input value={formData.project_id} onChange={(e) => setFormData({ ...formData, project_id: e.target.value })} disabled={editDialogOpen} placeholder="e.g., PROJ001" />
            </div>
            <div className="space-y-2">
              <Label>Project Name *</Label>
              <Input value={formData.project_name} onChange={(e) => setFormData({ ...formData, project_name: e.target.value })} placeholder="Project name" />
            </div>
            <div className="flex items-center justify-between">
              <Label>Active</Label>
              <Switch checked={formData.is_active} onCheckedChange={(v) => setFormData({ ...formData, is_active: v })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setCreateDialogOpen(false); setEditDialogOpen(false); resetForm(); }}>Cancel</Button>
            <Button onClick={editDialogOpen ? handleEdit : handleCreate} disabled={formLoading}>
              {formLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {editDialogOpen ? 'Save Changes' : 'Create Project'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Project</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{selectedProject?.project_name}"? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDelete} disabled={formLoading}>
              {formLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Allocate Employees Dialog */}
      <Dialog open={allocateDialogOpen} onOpenChange={setAllocateDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Allocate Employees to {selectedProject?.project_name}</DialogTitle>
            <DialogDescription>
              Select employees to assign to this project
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm text-muted-foreground">
                Selected: {selectedEmployees.length} employee(s)
              </span>
              <Button variant="ghost" size="sm" onClick={clearAllEmployees}>
                Clear All
              </Button>
            </div>
            <ScrollArea className="h-[300px] border rounded-md p-2">
              {employedEmployees.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  No employed employees available
                </div>
              ) : (
                <div className="space-y-2">
                  {employedEmployees.map(emp => (
                    <div 
                      key={emp.employee_id}
                      className="flex items-center space-x-3 p-2 rounded hover:bg-muted cursor-pointer"
                      onClick={() => toggleEmployee(emp.employee_id)}
                    >
                      <Checkbox 
                        checked={selectedEmployees.includes(emp.employee_id)}
                        onCheckedChange={() => toggleEmployee(emp.employee_id)}
                      />
                      <div className="flex-1">
                        <p className="font-medium">{emp.employee_name}</p>
                        <p className="text-xs text-muted-foreground">{emp.employee_id} • {emp.email}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAllocateDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleAllocateEmployees} disabled={formLoading}>
              {formLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Save Allocation
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProjectList;
